import { format, isValid } from "date-fns";
import { useState } from "react";
import type { FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { Button } from "../shared/Button/Button";
import { DateOfBirthInput } from "../CreateAccount/DateOfBirthInput";
import {
  BillingLinkVerificationAttemptsExhaustedCode,
  VerifyBillingLinkDob,
} from "./Services/UpfrontPaymentService";
import {
  BillingLinkVerificationCard,
  BillingLinkVerificationFallback,
  BillingLinkVerificationPage,
  BillingSupportLink,
} from "./BillingLinkVerification.styles";
import { BillingSupportNumber } from "./types";

type BillingLinkVerificationMode = "dob" | "denied" | "error";

interface BillingLinkVerificationProps {
  billingPK: string;
  mode: BillingLinkVerificationMode;
  onAuthorized: () => void;
  onRetry: () => void;
  onSignIn: () => void;
  onSignOut: () => void;
}

const descriptionKeyByMode = {
  dob: "upfrontPayment.verification.description",
  denied: "upfrontPayment.verification.wrongAccount",
  error: "upfrontPayment.verification.authorizationError",
} as const satisfies Record<BillingLinkVerificationMode, string>;

const titleKeyByMode = {
  dob: "upfrontPayment.verification.title",
  denied: "upfrontPayment.verification.accessDeniedTitle",
  error: "upfrontPayment.verification.accessDeniedTitle",
} as const satisfies Record<BillingLinkVerificationMode, string>;

export const BillingLinkVerification = ({
  billingPK,
  mode,
  onAuthorized,
  onRetry,
  onSignIn,
  onSignOut,
}: BillingLinkVerificationProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const dateOfBirth =
    selectedDate && isValid(selectedDate)
      ? format(selectedDate, "yyyy-MM-dd")
      : "";
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptsExhausted, setAttemptsExhausted] = useState(false);
  const phoneLink = (
    <BillingSupportLink href={`tel:${BillingSupportNumber.replace(/\D/g, "")}`}>
      {BillingSupportNumber}
    </BillingSupportLink>
  );
  const loginLink = (
    <BillingSupportLink href={LOGIN_PAGE_URL} onClick={onSignIn} />
  );
  const signOutLink = (
    <BillingSupportLink
      href={LOGIN_PAGE_URL}
      onClick={(event) => {
        event.preventDefault();
        onSignOut();
      }}
    />
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dateOfBirth) {
      setErrorMessage(
        t(
          selectedDate
            ? "createAccount.errors.invalidDate"
            : "upfrontPayment.verification.dobRequired",
        ),
      );
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);
    try {
      const response = await VerifyBillingLinkDob(billingPK, dateOfBirth);
      if (response.ok) {
        onAuthorized();
        return;
      }
      if (response.status === 429) {
        const error = (await response.json()) as {
          code?: string;
        };
        if (error.code === BillingLinkVerificationAttemptsExhaustedCode) {
          setAttemptsExhausted(true);
          return;
        }
      }
      setErrorMessage(t("upfrontPayment.verification.genericFailure"));
    } catch {
      setErrorMessage(t("upfrontPayment.verification.genericFailure"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleKey = attemptsExhausted
    ? "upfrontPayment.verification.attemptsExhaustedTitle"
    : titleKeyByMode[mode];

  return (
    <BillingLinkVerificationPage>
      <BillingLinkVerificationCard>
        <h1>{t(titleKey)}</h1>
        <p>
          <Trans
            i18nKey={
              attemptsExhausted
                ? "upfrontPayment.verification.attemptsExhausted"
                : descriptionKeyByMode[mode]
            }
            values={{ phone: BillingSupportNumber }}
            components={{ loginLink, phoneLink, signOutLink }}
          />
        </p>
        {mode === "dob" && !attemptsExhausted && (
          <form onSubmit={handleSubmit}>
            <DateOfBirthInput
              label={t("upfrontPayment.verification.dobLabel")}
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setErrorMessage("");
              }}
              error={errorMessage}
            />
            <Button
              label={
                isSubmitting
                  ? t("upfrontPayment.verification.verifying")
                  : t("upfrontPayment.verification.continue")
              }
              size="medium"
              disabled={isSubmitting}
              type="submit"
            />
          </form>
        )}
        {mode === "error" && (
          <Button
            label={t("upfrontPayment.verification.tryAgain")}
            size="medium"
            onClick={onRetry}
          />
        )}
        {attemptsExhausted && (
          <Button
            label={t("upfrontPayment.verification.tryAgain")}
            size="medium"
            onClick={() => {
              setAttemptsExhausted(false);
              setSelectedDate(null);
            }}
          />
        )}
        {mode !== "denied" && !attemptsExhausted && (
          <BillingLinkVerificationFallback>
            <Trans
              i18nKey="upfrontPayment.verification.fallback"
              values={{ phone: BillingSupportNumber }}
              components={{ loginLink, phoneLink }}
            />
          </BillingLinkVerificationFallback>
        )}
      </BillingLinkVerificationCard>
    </BillingLinkVerificationPage>
  );
};
